/**
 * Icon component using lucide-react.
 * Replaces window.Icon that used lucide.createIcons().
 */
import { type FC, type SVGProps } from 'react';
import * as icons from 'lucide-react';

// lucide-react v0.4xx+ exports ForwardRef objects (typeof === 'object')
// not plain functions, so we check for $$typeof or render
function isReactComponent(value: unknown): value is FC<SVGProps<SVGSVGElement>> {
  if (typeof value === 'function') return true;
  if (typeof value === 'object' && value !== null && '$$typeof' in value) return true;
  return false;
}

// Build a map of kebab-case icon names to PascalCase components
const iconMap: Record<string, FC<SVGProps<SVGSVGElement>>> = {};

for (const [key, component] of Object.entries(icons)) {
  if (isReactComponent(component) && key !== 'createLucideIcon' && !key.endsWith('Icon')) {
    // Convert PascalCase → kebab-case, also handle trailing digits (Wand2 → wand-2)
    const kebab = key
      .replace(/([A-Z])/g, '-$1')
      .replace(/(\d+)/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '')
      .replace(/--+/g, '-');
    iconMap[kebab] = component as FC<SVGProps<SVGSVGElement>>;
    // Also store original casing and lowercase
    iconMap[key] = component as FC<SVGProps<SVGSVGElement>>;
    iconMap[key.toLowerCase()] = component as FC<SVGProps<SVGSVGElement>>;
  }
}

interface IconProps extends SVGProps<SVGSVGElement> {
  name: string;
  className?: string;
}

export const Icon: FC<IconProps> = ({ name, className, ...props }) => {
  const LucideIcon = iconMap[name];
  if (!LucideIcon) {
    console.warn(`Icon "${name}" not found`);
    return <span className={className} />;
  }
  return <LucideIcon className={className} {...props} />;
};
